/**
 * De statussen van een deelnemer (Personas.Status) en hun Nederlandse labels.
 *
 * De volgorde is het traject dat een deelnemer doorloopt: aangemeld, actief,
 * eventueel inactief, uiteindelijk uitgestroomd. Die volgorde is
 * betekenisdragend — hij stuurt de balkvolgorde in elke statusgrafiek en de
 * volgorde van de tabs boven de deelnemerslijst (ADR-032 regel 1).
 *
 * Eén bron voor élke keuzelijst, tab, filterchip, grafiek-as en widget. Kopieer
 * de lijst niet — een kopie die een sleutel mist laat die deelnemers zonder
 * foutmelding verdwijnen. Tot 2026-09-09 stonden er zes van zulke kopieën in
 * `src/`; `personaStatusLabels.test.js` bewaakt nu dat er geen zevende bijkomt.
 *
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 */

/** @type {Record<string, string>} */
const PERSONA_STATUS_LABELS = {
    'pre-registration': 'Vooraanmelding',
    active: 'Actief',
    inactive: 'Inactief',
    outflow: 'Uitgestroomd',
}

/**
 * Dezelfde statussen, met 'Uitstroom' waar 'Uitgestroomd' niet past.
 *
 * Bestaat alleen om de breedte: op een grafiek-as duwt 'Uitgestroomd' het
 * label schuin of kapt het af. Het is dus een **schrijfwijze**, geen tweede
 * lijst — de test dwingt af dat hij op precies één sleutel afwijkt.
 *
 * Gebruikt door `countPersonasByStatus` — dat is sinds 2026-09-09 de enige
 * telling achter de twee statusgrafieken (dashboardkaart en
 * live-report-widget), dus die lezen deze schrijfwijze via de helper en niet
 * meer rechtstreeks. Twee keuzelijsten gebruiken hem wél rechtstreeks
 * (`DashboardPersonaFilter`, `PersonasTotalWidget`) — daar is de korte vorm
 * historisch en niet nodig; ze kunnen zonder gevolg naar de lange over, maar
 * dat is een zichtbare labelwijziging en hoort dus niet in een ontdubbeling.
 *
 * @type {Record<string, string>}
 */
const PERSONA_STATUS_SHORT_LABELS = {
    ...PERSONA_STATUS_LABELS,
    outflow: 'Uitstroom',
}

/** Elke statussleutel, in trajectvolgorde. @type {Array<string>} */
const PERSONA_STATUS_KEYS = Object.keys(PERSONA_STATUS_LABELS)

/**
 * @typedef {object} PersonaStatusOption
 * @property {string} value - De waarde in Personas.Status
 * @property {string} label - Het label dat de gebruiker ziet
 */

/** @type {Array<PersonaStatusOption>} */
const PERSONA_STATUS_OPTIONS = PERSONA_STATUS_KEYS
    .map((value) => ({ value, label: PERSONA_STATUS_LABELS[value] }))

/** @type {Array<PersonaStatusOption>} */
const PERSONA_STATUS_SHORT_OPTIONS = PERSONA_STATUS_KEYS
    .map((value) => ({ value, label: PERSONA_STATUS_SHORT_LABELS[value] }))

export {
    PERSONA_STATUS_LABELS,
    PERSONA_STATUS_SHORT_LABELS,
    PERSONA_STATUS_KEYS,
    PERSONA_STATUS_OPTIONS,
    PERSONA_STATUS_SHORT_OPTIONS,
}
